import { useState } from 'react';
import SEOMeta from '@/components/base/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于韩漫网',
    description: '韩漫网致力于为广大漫画爱好者提供最新、最全的韩国漫画在线阅读服务。无需注册，完全免费。',
    about: {
      '@type': 'Organization',
      name: '韩漫网',
      url: 'http://ynbskj.cn/',
      description: '韩漫网 - 免费韩国漫画在线阅读平台',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '望京SOHO T1-1206',
        addressLocality: '北京',
        addressRegion: '朝阳区',
        addressCountry: 'CN',
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          telephone: '+86-10-8888-6666',
          contactType: 'customer service',
          areaServed: 'CN',
          availableLanguage: 'zh-CN',
        },
        {
          '@type': 'ContactPoint',
          telephone: '+86-10-8888-7777',
          contactType: 'business cooperation',
          areaServed: 'CN',
          availableLanguage: ['zh-CN', 'en'],
        },
      ],
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://ynbskj.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://ynbskj.cn/about' },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫网需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。韩漫网上所有韩国漫画均免费阅读，无任何隐藏收费。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网是否需要下载APP？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。韩漫网是纯网页版，无需下载任何APP，打开浏览器即可阅读。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系韩漫网进行商务合作？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过页面底部的商务合作表单提交合作意向，或发送邮件至 hello@ynbskj.cn。我们的商务团队会在24小时内回复。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网有多少漫画资源？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网目前收录超过12,000部韩国漫画，涵盖热血、爱情、奇幻、耽美等丰富题材，每日持续更新扩充中。',
        },
      },
    ],
  };

  const values = [
    {
      icon: 'ri-book-open-line',
      title: '海量资源',
      desc: '收录超过12,000部韩国漫画，涵盖热血、爱情、奇幻、耽美等丰富题材，持续扩充中。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '完全免费',
      desc: '无需注册、无需付费、无需订阅。打开网站即可免费在线阅读，无任何隐藏消费。',
    },
    {
      icon: 'ri-device-line',
      title: '多端适配',
      desc: '完美适配手机、平板、电脑等多种设备，随时随地享受流畅的漫画阅读体验。',
    },
    {
      icon: 'ri-speed-up-line',
      title: '极速更新',
      desc: '每日同步更新热门连载，第一时间为你带来最新章节，追更不等待。',
    },
    {
      icon: 'ri-image-line',
      title: '高清画质',
      desc: '所有漫画均为高清画质，细节清晰，色彩还原精准，带来沉浸式阅读享受。',
    },
    {
      icon: 'ri-heart-line',
      title: '用心服务',
      desc: '专注漫画领域多年，持续优化阅读体验，用心为每一位漫迷提供最优质的阅读服务。',
    },
  ];

  const platformStats = [
    { icon: 'ri-user-star-line', value: '500万+', label: '注册用户', desc: '覆盖全球中文用户群体' },
    { icon: 'ri-book-read-line', value: '12,000+', label: '漫画资源', desc: '丰富题材每日持续更新' },
    { icon: 'ri-eye-line', value: '3.2亿+', label: '月均阅读量', desc: '用户日均停留超45分钟' },
    { icon: 'ri-thumb-up-line', value: '98.6%', label: '用户好评率', desc: '来自百万用户的真实反馈' },
  ];

  const milestones = [
    { year: '2020', event: '韩漫网正式上线，首批上线5,000部漫画' },
    { year: '2021', event: '用户突破100万，新增耽美、恐怖等分类' },
    { year: '2022', event: '漫画资源突破8,000部，推出移动端适配' },
    { year: '2023', event: '用户突破300万，日活超过50万' },
    { year: '2024', event: '漫画资源突破10,000部，全面优化阅读体验' },
    { year: '2025', event: '用户突破500万，成为国内领先的韩漫阅读平台' },
    { year: '2026', event: '持续扩展资源库，探索AI智能推荐等新功能' },
  ];

  const handleCoopSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');

    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      formData.delete('phone_alt');
      const response = await fetch('http://ynbskj.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 品牌介绍、平台数据与商务合作"
        description="韩漫网品牌介绍：致力于为漫画爱好者提供免费韩国漫画在线阅读服务。了解我们的品牌理念、用户口碑、平台数据、商务合作联系方式。超过500万用户的选择。"
        keywords="韩漫网,关于我们,品牌介绍,用户口碑,联系方式,免费韩漫,韩国漫画平台,商务合作"
        canonicalUrl="http://ynbskj.cn/about"
        jsonLd={[aboutLd, breadcrumbLd, faqLd]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>

            {/* Hero / Brand Story */}
            <div className="flex flex-col lg:flex-row gap-0">
              <div className="lg:w-1/2 relative min-h-[350px] md:min-h-[500px] overflow-hidden rounded-xl">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20dark%20atmospheric%20Korean%20manhwa%20style%20illustration%20showing%20a%20cozy%20reading%20space%2C%20bookshelves%20with%20comics%2C%20warm%20ambient%20lighting%2C%20immersive%20and%20artistic%20vibe%2C%20professional%20digital%20art%2C%20dark%20background%20with%20warm%20accents&width=900&height=700&seq=about-hero-image&orientation=landscape"
                  alt="韩漫网品牌形象"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-background-50/80"></div>
                <div className="absolute top-6 left-6">
                  <span className="inline-block px-3 py-1 text-[11px] font-label font-medium text-foreground-400 border border-foreground-50/20 rounded-full whitespace-nowrap">
                    品牌故事
                  </span>
                </div>
                <div className="absolute bottom-8 left-6">
                  <h1 className="text-3xl md:text-5xl font-heading font-black text-foreground-50 leading-tight">
                    关于
                    <br />
                    韩漫网
                    <br />
                    我们的故事
                  </h1>
                </div>
              </div>

              <div className="lg:w-1/2 flex flex-col items-center justify-center px-6 py-14 md:py-20 bg-background-100 lg:rounded-r-xl">
                <div className="max-w-md text-center">
                  <h2 className="text-2xl md:text-4xl font-heading font-black text-foreground-50 leading-tight">
                    为漫迷而生
                    <br />
                    因热爱而坚持
                  </h2>
                  <p className="mt-5 text-sm text-foreground-500 leading-relaxed">
                    韩漫网成立于2020年，由一群热爱韩国漫画的年轻人创立。我们深知漫迷在寻找高质量免费漫画资源时的困扰，因此立志打造一个无需注册、完全免费的韩国漫画在线阅读平台。经过多年的发展，韩漫网已成为拥有超过500万用户的知名韩漫阅读平台。
                  </p>
                  <p className="mt-4 text-sm text-foreground-500 leading-relaxed">
                    我们始终坚持「免费、便捷、丰富」的核心理念，不断扩充漫画资源库，优化阅读体验，让每一位漫迷都能在这里找到心仪的作品。
                  </p>
                </div>
              </div>
            </div>

            {/* Platform Stats */}
            <div className="mt-16 md:mt-20">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                  <span className="text-[11px] font-label font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">
                    平台数据
                  </span>
                </div>
                <h2 className="text-2xl md:text-4xl font-heading font-black text-foreground-50">
                  用数据说话
                </h2>
                <p className="mt-2 text-sm text-foreground-500">韩漫网六年成长之路</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {platformStats.map((stat) => (
                  <div key={stat.label} className="bg-background-100 rounded-lg p-6 md:p-7 text-center hover:bg-background-200/50 transition-colors">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400 mb-4">
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <p className="text-3xl md:text-4xl font-heading font-black text-foreground-50">{stat.value}</p>
                    <p className="mt-1 text-sm font-heading font-bold text-foreground-100">{stat.label}</p>
                    <p className="mt-1.5 text-[11px] text-foreground-500">{stat.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Development Milestones */}
            <div className="mt-16 md:mt-20">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-4xl font-heading font-black text-foreground-50">
                  发展<span className="text-accent-400">历程</span>
                </h2>
                <p className="mt-2 text-sm text-foreground-500">不忘初心，砥砺前行</p>
              </div>

              <div className="max-w-3xl mx-auto">
                {milestones.map((m, i) => (
                  <div key={m.year} className="flex items-start gap-5 pb-8 relative">
                    {i < milestones.length - 1 && (
                      <div className="absolute left-[29px] top-14 bottom-0 w-px bg-background-300/40"></div>
                    )}
                    <div className="w-[60px] shrink-0 text-right">
                      <span className="text-lg font-heading font-black text-primary-400">{m.year}</span>
                    </div>
                    <div className="flex-1 bg-background-100 rounded-lg p-4 md:p-5">
                      <p className="text-sm text-foreground-200 leading-relaxed">{m.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Our Advantages */}
            <div className="mt-16 md:mt-20">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-4xl font-heading font-black text-foreground-50">
                  我们的<span className="text-primary-400">优势</span>
                </h2>
                <p className="mt-2 text-sm text-foreground-500">选择韩漫网的六大理由</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {values.map((v) => (
                  <div
                    key={v.title}
                    className="bg-background-100 rounded-lg p-6 md:p-7 hover:bg-background-200/50 transition-colors"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 mb-4">
                      <i className={`${v.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-heading font-bold text-foreground-50 mb-2">{v.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* User Testimonials */}
            <div className="mt-16 md:mt-20 bg-background-100 rounded-xl p-8 md:p-12">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
                <div className="lg:w-[38%]">
                  <span className="inline-block px-3 py-1 text-[11px] font-label font-medium text-foreground-500 border border-background-300/60 rounded-full whitespace-nowrap">
                    用户口碑
                  </span>
                  <h2 className="mt-5 text-2xl md:text-4xl font-heading font-black text-foreground-50 leading-tight">
                    漫迷们的
                    <br />
                    <span className="text-accent-400">真实评价</span>
                  </h2>
                  <p className="mt-6 text-sm text-foreground-500 leading-relaxed max-w-sm">
                    来自真实用户的反馈，是我们不断前进的动力。每一个好评都是对我们工作的最大肯定。
                  </p>
                  <div className="flex items-center gap-6 mt-8">
                    <div>
                      <p className="text-3xl md:text-4xl font-heading font-black text-foreground-50">4.9</p>
                      <p className="text-xs text-foreground-500 mt-1">平均评分</p>
                    </div>
                    <div>
                      <p className="text-3xl md:text-4xl font-heading font-black text-foreground-50">98%</p>
                      <p className="text-xs text-foreground-500 mt-1">好评率</p>
                    </div>
                  </div>
                </div>

                <div className="lg:w-[62%] grid grid-cols-1 md:grid-cols-2 gap-4">
                  {testimonials.slice(0, 4).map((t) => (
                    <div key={t.id} className="bg-background-200/30 rounded-lg p-5">
                      <div className="flex items-center gap-0.5 mb-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <div key={i} className="w-4 h-4 flex items-center justify-center">
                            <i
                              className={`text-sm ${
                                i < t.rating ? 'ri-star-fill text-primary-400' : 'ri-star-line text-foreground-700'
                              }`}
                            ></i>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-foreground-500 leading-relaxed">{t.comment.slice(0, 100)}...</p>
                      <div className="flex items-center gap-2.5 mt-3 pt-3 border-t border-background-300/20">
                        <div className="w-7 h-7 rounded-full overflow-hidden">
                          <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                        </div>
                        <span className="text-xs font-semibold text-foreground-300">{t.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Business Cooperation */}
            <div className="mt-16 md:mt-20">
              <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
                <div className="lg:w-[38%]">
                  <span className="inline-block px-3 py-1 text-[11px] font-label font-medium text-foreground-500 border border-background-300/60 rounded-full whitespace-nowrap">
                    商务合作
                  </span>
                  <h2 className="mt-5 text-2xl md:text-4xl font-heading font-black text-foreground-50 leading-tight">
                    期待与您
                    <br />
                    <span className="text-primary-400">携手共赢</span>
                  </h2>
                  <p className="mt-6 text-sm text-foreground-500 leading-relaxed max-w-sm">
                    我们欢迎各类商务合作，包括但不限于：广告投放、内容授权、IP合作、品牌联名、技术合作等。请填写表单，我们的商务团队会在24小时内与您联系。
                  </p>
                  <div className="space-y-5 mt-8">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 shrink-0">
                        <i className="ri-mail-send-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground-100">商务邮箱</p>
                        <p className="text-xs text-foreground-400">hello@ynbskj.cn</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground-100">商务电话</p>
                        <p className="text-xs text-foreground-400">+86 10-8888-7777</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-foreground-100">工作时间</p>
                        <p className="text-xs text-foreground-400">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:w-[62%]">
                  <form
                    onSubmit={handleCoopSubmit}
                   
                    className="bg-background-100 rounded-xl p-6 md:p-8 space-y-5"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="coop-name" className="block text-xs font-semibold text-foreground-200 mb-2">
                          联系人姓名 <span className="text-primary-400">*</span>
                        </label>
                        <input
                          id="coop-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-3 rounded-lg bg-background-200/40 border border-background-300/40 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/60 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="coop-company" className="block text-xs font-semibold text-foreground-200 mb-2">
                          公司名称
                        </label>
                        <input
                          id="coop-company"
                          name="company"
                          type="text"
                          className="w-full px-4 py-3 rounded-lg bg-background-200/40 border border-background-300/40 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/60 transition-colors"
                          placeholder="请输入公司名称"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="coop-email" className="block text-xs font-semibold text-foreground-200 mb-2">
                          电子邮箱 <span className="text-primary-400">*</span>
                        </label>
                        <input
                          id="coop-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-3 rounded-lg bg-background-200/40 border border-background-300/40 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/60 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="coop-phone" className="block text-xs font-semibold text-foreground-200 mb-2">
                          联系电话
                        </label>
                        <input
                          id="coop-phone"
                          name="phone"
                          type="tel"
                          className="w-full px-4 py-3 rounded-lg bg-background-200/40 border border-background-300/40 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/60 transition-colors"
                          placeholder="请输入联系电话"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="coop-type" className="block text-xs font-semibold text-foreground-200 mb-2">
                        合作类型 <span className="text-primary-400">*</span>
                      </label>
                      <select
                        id="coop-type"
                        name="cooperation_type"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-200/40 border border-background-300/40 text-sm text-foreground-100 focus:outline-none focus:border-primary-400/60 transition-colors cursor-pointer"
                      >
                        <option value="">请选择合作类型</option>
                        <option value="广告投放">广告投放</option>
                        <option value="内容授权">内容授权</option>
                        <option value="IP合作">IP合作</option>
                        <option value="品牌联名">品牌联名</option>
                        <option value="技术合作">技术合作</option>
                        <option value="其他">其他合作</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="coop-message" className="block text-xs font-semibold text-foreground-200 mb-2">
                        合作说明 <span className="text-primary-400">*</span>
                      </label>
                      <textarea
                        id="coop-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-3 rounded-lg bg-background-200/40 border border-background-300/40 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-400/60 transition-colors resize-none"
                        placeholder="请简要描述您的合作意向和需求（500字以内）"
                      ></textarea>
                    </div>

                    {/* Honeypot */}
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="anti-spam-field"
                    />

                    <div className="flex items-start gap-3">
                      <input
                        id="coop-agree"
                        name="agree"
                        type="checkbox"
                        required
                        className="mt-0.5 shrink-0 accent-primary-500 cursor-pointer"
                      />
                      <label htmlFor="coop-agree" className="text-[11px] text-foreground-500 leading-relaxed cursor-pointer">
                        我同意韩漫网的隐私政策和服务条款，并允许韩漫网通过以上联系方式与我取得联系。
                      </label>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-950 font-semibold text-sm hover:bg-primary-400 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中...
                        </>
                      ) : (
                        <>
                          提交合作意向
                          <i className="ri-arrow-right-line text-base"></i>
                        </>
                      )}
                    </button>

                    {formStatus === 'success' && (
                      <div className="flex items-center gap-2 p-4 rounded-lg bg-accent-500/10 border border-accent-500/20">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                          <i className="ri-checkbox-circle-fill text-accent-400"></i>
                        </div>
                        <p className="text-xs text-foreground-200">感谢您的合作意向！我们的商务团队会在24小时内与您联系。</p>
                      </div>
                    )}

                    {formStatus === 'error' && (
                      <div className="flex items-center gap-2 p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0">
                          <i className="ri-error-warning-fill text-red-400"></i>
                        </div>
                        <p className="text-xs text-red-300">{formError}</p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>

            {/* Contact Info Cards */}
            <div className="mt-16 md:mt-20">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-4xl font-heading font-black text-foreground-50">
                  联系我们
                </h2>
                <p className="mt-2 text-sm text-foreground-500">有任何问题或建议？欢迎随时联系</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-5 max-w-4xl mx-auto">
                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 mb-4">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-50 mb-2">公司地址</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    北京市朝阳区
                    <br />
                    望京SOHO T1-1206
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 mb-4">
                    <i className="ri-phone-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-50 mb-2">客服电话</h3>
                  <p className="text-xs text-foreground-500">+86 10-8888-6666</p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-500/15 text-primary-400 mb-4">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-50 mb-2">客服邮箱</h3>
                  <p className="text-xs text-foreground-500">hello@ynbskj.cn</p>
                </div>
                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-500/15 text-accent-400 mb-4">
                    <i className="ri-briefcase-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-50 mb-2">商务合作</h3>
                  <p className="text-xs text-foreground-500">hello@ynbskj.cn</p>
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}